import * as dc from "@/components/layout/list/defaultType";
import { ClipboardProp, DataListParams, DataListProp } from "../../layoutType";
import {
  convertFormValues,
  getDateDiff,
  getFormDateDiff,
  getInputValueCheck,
  runFormFunction,
  setFormClass,
} from "../../input/inputFunction";
import dayjs from "dayjs";
import { Debug } from "@/util/debug";
import { fetchHost } from "@/util/fetchutil";
import { DataColumnRecord } from "../../input/inputType";
import { getTranslate } from "@/util/arrayutil";
import { checkEmpty, padBase64 } from "@/util/stringutil";
import { qcCommonCols } from "../../inputCols/qcCols";
import { brewingChangeCalc, brewingDefault, brewingInit } from "../../inputCols/brewingCols";
import { beaninfoSelectList, getBeaninfoList } from "../../inputCols/beaninfoCols";
import Icon from "@/components/common/icon/icon";
import { enLocale, koLocale } from "@/i18n/routing";

export const qcResultClipboard = (formFunction: Record<string, Record<string, Function>> | undefined): ClipboardProp => {
  return {
    category: "QCResult",
    exField: ["roaster_seq", "cupnote_seq", "brewing_seq"],
    pasteMatching: ["QCCommon", "BrewingCommon"],
    load: (data) => {
      // Debug(data);
      [
        "cupnote_balance_point",
        "cupnote_aroma_point",
        "cupnote_sweetness_point",
        "cupnote_body_point",
        "cupnote_flavor_point",
        "cupnote_acidity_point",
      ].forEach((item) => runFormFunction(formFunction, item, "inputSync"));
      runFormFunction(formFunction, "cupnote_radar", "changeData");
    },
  };
};

export const qcNamesCols = (locale?: string): DataColumnRecord => {
  return {
    cupnote_name: {
      ...dc.inputFull,
      label: locale === enLocale ? "cupnote_name" : "cupnote_name_locale",
      labelClassName: dc.strongLabelClassName,
      noItem: false,
      showCount: true,
      required: true,
      maxLength: 300,
    },
    store_name_main: {
      label: locale === enLocale ? "store_name" : "store_name_locale",
      valueClassName: "flex-col md:flex-row mobile_multi",
      read: { display: "notNull", displayCheckIds: ["store_name"] },
      fieldId: {
        store_name_dummy: {
          fieldId: { store_name: { ...dc.inputFull, maxLength: 300, showCount: true } },
        },
        cupnote_place_dummy: {
          flexClassName: "ml-0 mt-2 md:ml-2 md:mt-0",
          fieldId: {
            cupnote_place: {
              ...dc.select,
              codeGroup: "NTPLC",
              inputClassName: dc.inputSelectWidth,
              placeholder: "cupnote_place",
              selectPropOption: { reloadButton: false },
              required: true,
            },
          },
        },
      },
    },
    cupnote_seq: dc.hidden,
  };
};

export const UserQC = (props: DataListProp): DataListParams | undefined => {
  const t = props.t;
  const ta = props.ta;
  const result = props.result;
  const params = props.params;
  const form = props.form;
  const formMemo = props.formMemo;
  const setValueList = props.setValueList;
  const formFunction = props.formFunction;
  const dataFunction = props.dataFunction;
  const alertContext = props.alertContext;

  const utcOffset = dayjs().utcOffset() * 60000;

  const qcSubCols: DataColumnRecord = {
    //rgst_at:{...dc.},
    rgst_by: { ...dc.rgst },
    //edit_at:{...dc.},
    edit_by: { ...dc.edit },
  };

  const qcClipboard: ClipboardProp = {
    category: "QCCommon",
    exField: ["roaster_seq", "cupnote_seq"],
    load: (data) => {
      // Debug(data);
      [
        "cupnote_balance_point",
        "cupnote_aroma_point",
        "cupnote_sweetness_point",
        "cupnote_body_point",
        "cupnote_flavor_point",
        "cupnote_acidity_point",
      ].forEach((item) => runFormFunction(formFunction?.current, item, "inputSync"));
      runFormFunction(formFunction?.current, "cupnote_radar", "changeData");
    },
  };

  const checkCoffeebeanUuid = () => {
    const { coffeebean_uuid_dummy: change_uuid, coffeebean_uuid_type: coffeebean_type } = form?.current?.getFieldsValue([
      "coffeebean_uuid_dummy",
      "coffeebean_uuid_type",
    ]);
    if (!checkEmpty(change_uuid) && !checkEmpty(coffeebean_type)) {
      const pad_uuid = padBase64(change_uuid);
      const fetch_params: { url: string; body: Record<string, any> } = { url: "", body: { check_usernote: 1 } };
      switch (coffeebean_type) {
        case "CAFEDF":
          fetch_params.url = "/api/info/cafe/bean/default";
          fetch_params.body.coffeebean_uuid = pad_uuid;
          break;
        case "CAFEDT":
          fetch_params.url = "/api/info/cafe/bean/detail";
          fetch_params.body.received_uuid = pad_uuid;
          break;
        case "RSTRDF":
          fetch_params.url = "/api/info/roaster/bean/default";
          fetch_params.body.blending_uuid = pad_uuid;
          break;
        case "RSTRDT":
          fetch_params.url = "/api/info/roaster/bean/detail";
          fetch_params.body.blending_result_uuid = pad_uuid;
          break;
      }
      const fetch_option = { ...dc.fetchOption };
      fetch_option["body"] = JSON.stringify(fetch_params.body);
      fetchHost(fetch_params.url, fetch_option, undefined, (data: any) => {
        if (form?.current && data?.result && data.result.detail_result) {
          const result = data.result.blending_result
            ? { ...data.result.detail_result, ...data.result.blending_result }
            : data.result.detail_result;
          if (data.result.greenbean_list && !checkEmpty(data.result.greenbean_list)) {
            result.greenbean_list = data.result.greenbean_list;
          }
          Debug(data.result, result);
          const infoList = checkEmpty(result.greenbean_list) ? undefined : getBeaninfoList(result.greenbean_list, false);
          const blending_country_code = result.blending_country_list ? result.blending_country_list.split(",")[0] : undefined;

          const infoValues: Record<string, any> = infoList
            ? {
                blending_country_list: infoList.greenbean_country_list,
                blending_country_code: infoList.greenbean_country_code,
                blending_region_seq: infoList.greenbean_region_list,
                blending_region_etc: infoList.greenbean_region_etc,
                blending_farm_seq: infoList.greenbean_farm_list,
                blending_farm_etc: infoList.greenbean_farm_etc,
                blending_variety_list: infoList.greenbean_variety_list,
                blending_variety_etc: infoList.greenbean_variety_etc,
                blending_grade_list: infoList.greenbean_grade_list,
                blending_grade_detail_list: infoList.greenbean_grade_detail_list,
                blending_process_list: infoList.greenbean_process_list,
                blending_process_etc: infoList.greenbean_process_etc,
              }
            : {
                blending_country_list: result.blending_country_list,
                blending_country_code:
                  result.blending_country_list && typeof result.blending_country_list === "string"
                    ? result.blending_country_list.split(",")[0]
                    : undefined,
                blending_region_seq: result.blending_region_seq,
                blending_region_etc: result.blending_region_etc,
                blending_farm_seq: result.blending_farm_seq,
                blending_farm_etc: result.blending_farm_etc,
                blending_variety_list: result.blending_variety_list,
                blending_variety_etc: result.blending_variety_etc,
                blending_grade_list: result.blending_grade_list,
                blending_grade_detail_list: result.blending_grade_detail_list,
                blending_process_list: result.blending_process_list,
                blending_process_etc: result.blending_process_etc,
              };
          const info_detail = Object.keys(infoValues).some((item) => !checkEmpty(infoValues[item]));

          const setValues: Record<string, any> = {
            coffeebean_uuid: change_uuid,
            store_name: params?.locale === enLocale ? result.store_name : result.store_name_locale,
            cupnote_name: params?.locale === enLocale ? result.blending_name : result.blending_name_locale,
            blending_result_date: result.blending_result_date ? dayjs(result.blending_result_date, "YYYY-MM-DD") : undefined,
            blending_loss: !checkEmpty(result.blending_loss) ? result.blending_loss / 10 : undefined,
            blending_organic_loss: !checkEmpty(result.blending_organic_loss) ? result.blending_organic_loss / 10 : undefined,
            blending_info_detail_use: info_detail,
            ...infoValues,
          };

          form.current.setFieldsValue(setValues);
          form.current.setFieldValue("brewing_date_term", getFormDateDiff(form?.current, "blending_result_date", "cupnote_date"));
          if (!checkEmpty(infoValues.blending_country_code)) {
            setTimeout(() => {
              runFormFunction(formFunction?.current, ["blending_region_seq", "blending_farm_seq"], "changeLoad");
            }, dc.setTimeoutShort);
          }
          setFormClass(dataFunction?.current, { hidden_blending_info: !info_detail });
        }
      });
    }
  };

  switch (params?.page.page ?? "") {
    //추출 컵노트
    //--info--{
    //--info--"table":{"system_TABLE_SCHEMA":"clm_user","system_TABLE_NAME":"cupnote_coffeebean","system_table_pk":["user_seq","cupnote_date","cupnote_seq"],"system_table_manual_increment":"cupnote_seq","system_table_page":"UserQCCoffeebean","system_table_path":"user/qc/coffeebean","system_table_page_name":"UserQCCoffeebean","system_table_auth":["SADMIN","ADMIN","USER"],"system_select_auth":[],"system_table_mapper":"userqc","system_table_detail_result":"detail_result","system_table_search_column":["cupnote_date","coffeebean_uuid_type","cupnote_satis"],"system_table_search_select":["cupnote_name"],"system_table_order_column":["cupnote_date","coffeebean_uuid_type","cupnote_name"],"system_table_default_name_column":"cupnote_name"}
    //--info--}

    //추출결과
    //--info--{
    //--info--"table":{"system_TABLE_SCHEMA":"clm_user","system_TABLE_NAME":"brewing_result","system_table_pk":["user_seq","cupnote_date","cupnote_seq"],"system_table_page":"UserQCBrewingResult","system_table_path":"user/qc/coffeebean","system_table_page_name":"UserQCBrewingResult","system_table_auth":["SADMIN","ADMIN","USER"],"system_select_auth":[],"system_table_mapper":"userqc","system_table_detail_result":"detail_result"}
    //--info--}

    //추출단계
    //--info--{
    //--info--"table":{"system_TABLE_SCHEMA":"clm_user","system_TABLE_NAME":"brewing_result_step","system_table_pk":["user_seq","cupnote_date","cupnote_seq"],"system_table_page":"UserQCBrewingResultStep","system_table_path":"user/qc/coffeebean","system_table_page_name":"UserQCBrewingResultStep","system_table_auth":["SADMIN","ADMIN","USER"],"system_select_auth":[],"system_table_mapper":"userqc","system_table_detail_result":"detail_result"}
    //--info--}

    case "UserQCCoffebean":
      return {
        ...result,
        dataFetch: { fetchUrl: "/api/user/qc/coffeebean/detail/", saveFetch: { fetchUrl: "/api/user/qc/coffeebean/edit" } },
        listFetch: { fetchUrl: "/api/user/qc/coffeebean/list" },
        dataResultName: "detail_result",
        dataMerge: "detail_result_step",
        dataName: "QCBrewingResultData",
        // dataFormClassName: "hidden_blending_info",
        dataFormClassDefault: ["hidden_blending_info", "hidden_step_detail", "hidden_brewing_result", "hidden_cupnote_flavor_wheel"],
        dataScrollAnimate: false,
        editCheck: true,
        editReload: true,
        clipboard: qcResultClipboard(formFunction?.current),
        timer: dc.brewingTimer,
        dataInit: (data: any, params: any, mode?: string) => {
          const checkData = data ?? {};
          if (params?.setTimer) {
            params.setTimer({ timer: dc.brewingTimer, start: undefined });
          }
          const setValues: Record<string, any> = {
            brewing_date_term: getDateDiff(checkData.blending_result_date, checkData.cupnote_date),
          };
          setFormClass(dataFunction?.current, {
            hidden_blending_info: !((checkData["blending_info_detail_use"] ?? 0) === 1),
            brewing_detail_use: checkData["brewing_detail_use"] ?? false,
            brewing_espresso_use: checkData["brewing_type"] === "ESP",
            brewing_drip_use: checkData["brewing_type"] === "DRP",
            hidden_step_detail: !((checkData["brewing_step_use"] ?? 0) === 1),
            hidden_brewing_result: !((checkData["brewing_result_use"] ?? 0) === 1),
            satis_detail_use: checkData["cupnote_satis_detail"],
            hidden_cupnote_flavor_wheel: !((checkData["cupnote_flavor_wheel_use"] ?? 0) === 1),
          });

          if (form?.current) {
            const coffeebean_uuid = data.coffeebean_uuid;
            if (checkEmpty(form.current.getFieldValue("coffeebean_uuid_dummy")) && !checkEmpty(coffeebean_uuid)) {
              setValues.coffeebean_uuid_dummy = coffeebean_uuid;
            }
            if (checkData.blending_country_list) {
              if (typeof checkData.blending_country_list === "string") {
                Debug("UserQCCoffebean Data", mode, data, setValues);
                checkData.blending_country_list = checkData.blending_country_list.split(",");
                if (checkData.blending_country_list.length === 1) {
                  setValues.blending_country_code = checkData.blending_country_list[0];
                }
              } else if (checkData.blending_country_list instanceof Array && checkData.blending_country_list.length === 1) {
                setValues.blending_country_code = checkData.blending_country_list[0];
              }
            }
            Debug("UserQCCoffebean Data", mode, data, checkData, setValues);
            form.current.setFieldsValue(setValues);
          }
          const country_code =
            setValues.blending_country_code ??
            (checkData.blending_country_list instanceof Array && checkData.blending_country_list.length > 0
              ? checkData.blending_country_list[0]
              : undefined);
          setTimeout(() => {
            if (setValues.blending_country_code) {
              runFormFunction(formFunction?.current, ["blending_region_seq", "blending_farm_seq"], "changeLoad");
            }
            setTimeout(() => {
              if (country_code) {
                runFormFunction(formFunction?.current, "blending_country_map", "setSelectCountry", [country_code]);
                setTimeout(() => {
                  runFormFunction(formFunction?.current, "blending_country_map", "setSelectCountry", [country_code]);
                }, dc.setTimeoutDelay * 4);
              }
            }, dc.setTimeoutShort);
            brewingChangeCalc(form?.current, dataFunction?.current);
          }, dc.setTimeoutShort);

          if ((checkData["cupnote_flavor_wheel_use"] ?? 0) === 1) {
            const check_wheel = { cnt: 0, find: false };
            const timer = setInterval(() => {
              const objs = document.querySelectorAll("form.ant-form .rt-Flex.data-chart.FlavorWheel svg > g > g");
              check_wheel.cnt++;
              if (objs && objs.length > 0) {
                check_wheel.find = true;
                runFormFunction(formFunction?.current, "cupnote_flavor_wheel", "changeData");
                const rotate =
                  getInputValueCheck(form?.current, formMemo?.current, dataFunction?.current, data, "cupnote_flavor_wheel_rotate") ?? 0;
                Debug("qcCommonInit", rotate);
                const objs = document.querySelectorAll("form.ant-form .rt-Flex.data-chart.FlavorWheel svg > g > g");
                objs.forEach((g) => {
                  const obj = g as SVGGElement;
                  const ctm = getTranslate(obj);
                  if (ctm) {
                    obj.setAttribute("transform", `translate(${ctm.x}, ${ctm.y}) rotate(${rotate})`);
                  }
                });
              }
              Debug(check_wheel);
              if (check_wheel.find || check_wheel.cnt > 4) {
                clearInterval(timer);
              }
            }, dc.setTimeoutDelay);
          }
        },
        dataCols: {
          ...qcNamesCols(params?.locale),
          coffeebean_serail: {
            label: "product_serial",
            // labelClassName: dc.mobileHidden,
            read: { display: "notNull", displayCheckIds: ["coffeebean_uuid"] },
            valueClassName: "flex-col md:flex-row mobile_multi",
            fieldId: {
              coffeebean_serail_uuid: {
                fieldId: {
                  coffeebean_uuid_dummy: {
                    ...dc.input,
                    fieldId: "coffeebean_uuid",
                    inputClassName: dc.inputCountryClassName,
                    widthFull: false,
                    saveData: false,
                  },
                },
              },
              coffeebean_serail_type: {
                flexClassName: "ml-0 mt-2 md:ml-2 md:mt-0",
                fieldId: {
                  coffeebean_uuid_type: {
                    ...dc.select,
                    codeGroup: "BNDV",
                    inputClassName: dc.inputSelectWidth,
                    create: {
                      dataProp: {
                        load: () => {
                          checkCoffeebeanUuid();
                        },
                      },
                    },
                    changePass: true,
                    selectPropOption: { reloadButton: false },
                  },
                  coffeebean_uuid_check: {
                    inputType: "element",
                    elementProp: {
                      element: <Icon iconName="Check" iconType="" />,
                      elementType: "button",
                      transition: true,
                      buttonProp: {
                        className: "input_ml px-2",
                        onClick: checkCoffeebeanUuid,
                      },
                    },
                    read: { display: false },
                    saveData: false,
                  },
                },
              },
            },
          },
          coffeebean_uuid: {
            ...dc.input,
            itemClassName: "hidden",

            inputClassName: dc.inputCountryClassName,
            widthFull: false,
            disabled: true,
            changePass: true,
            read: { display: "notNull" },
          },
          coffeebean_serail_guide: { ...dc.guideDefault, fixedValue: "coffeebean_serail_guide", read: { display: false } },
          blending_result_date_dummy: {
            label: "blending_result_date",
            // itemClassName: "hidden",
            read: { display: "notNull", displayCheckIds: ["blending_result_date"] },
            fieldId: {
              blending_result_date: {
                ...dc.date,
                noItem: false,
                onChange: () => {
                  form?.current?.setFieldValue("brewing_date_term", getFormDateDiff(form?.current, "blending_result_date", "cupnote_date"));
                },
              },
              brewing_date_term: {
                ...dc.dayNumber,

                noItem: false,
                readOnly: true,
                tooltip: "brewing_date_term",
              },
            },
          },
          cupnote_date: {
            ...dc.date,
            noItem: false,
            placeholder: "cupnote_date",
            create: { defaultValue: dayjs().format("YYYY-MM-DD") },
            update: { disabled: true },
            onChange: (e) => {
              form?.current?.setFieldValue("brewing_date_term", getFormDateDiff(form?.current, "blending_result_date", "cupnote_date"));
            },
          },
          // blending_result_select: {
          //       label: "blending_result_select_label",
          //       labelClassName: dc.strongLabelClassName,
          //       fieldId: {
          //       },
          //     },
          blending_info_detail_div: {
            label: "blending_info_detail_use",
            read: { display: false },
            fieldId: {
              blending_info_detail_use: {
                ...dc.use,
                inputClassName: "my-auto",
                read: { display: false },
                create: { defaultValue: false },
                onChange: (e) => {
                  setFormClass(dataFunction?.current, { hidden_blending_info: !e.value });
                },
                changePass: true,
              },
              blending_info_detail_button: {
                ...dc.textButton("ArrowsOut", "brewing_expand_all", () => {
                  const expand_all_value = {
                    blending_info_detail_use: 1,
                    brewing_result_use: 1,
                    brewing_detail_use: 1,
                    brewing_step_use: 1,
                    cupnote_satis_detail: 1,
                    cupnote_flavor_wheel_use: 1,
                  };
                  form?.current?.setFieldsValue(expand_all_value);
                  setFormClass(dataFunction?.current, {
                    hidden_blending_info: false,
                    hidden_brewing_result: false,
                    hidden_step_detail: false,
                    brewing_detail_use: true,
                    satis_detail_use: true,
                    hidden_cupnote_flavor_wheel: false,
                  });
                }),
                read: { display: false },
              },
            },
          },
          blending_info_detail_guide: { ...dc.guideDefault, fixedValue: "blending_info_detail_guide", read: { display: false } },
          ...beaninfoSelectList(form, formFunction?.current, params?.locale, true),
          div_0: { ...dc.itemDiv, itemClassName: dc.itemDiv.itemClassName + " brewing_result" },
          brewing_result_use: {
            ...dc.use,
            itemClassName: "blending_info",
            inputClassName: "my-auto",
            read: { display: false },
            create: { defaultValue: false },
            onChange: (e) => {
              setFormClass(dataFunction?.current, { hidden_brewing_result: !e.value });
            },
            changePass: true,
          },
          brewing_seq: {
            ...dc.brewingSelect(params?.locale, false),
            itemClassName: "brewing_result",
            label: "brewing_select_label",
            dataUrl: "/api/user/brewing/default/code",
            onChange: (data) => {
              const temp_result = runFormFunction(formFunction?.current, "brewing_seq", "selectData");
              Debug(temp_result);
              const select_data = temp_result.result;
              if (select_data) {
                const convert_values = convertFormValues(formMemo?.current, select_data, undefined, []);
                if (convert_values) {
                  convert_values["brewing_result_use"] = 1;
                  if (checkEmpty(form?.current?.getFieldValue("cupnote_date"))) convert_values["cupnote_date"] = dayjs();
                  form?.current?.setFieldsValue(convert_values);
                  setTimeout(() => {
                    runFormFunction(formFunction?.current, ["blending_seq", "blending_result_seq"], "changeLoad");
                  }, dc.setTimeoutShort);
                }
                setFormClass(dataFunction?.current, {
                  hidden_brewing_result: !true,
                });
                brewingInit(form?.current, formFunction?.current, dataFunction?.current, select_data, params?.setTimer);
              }
            },
            read: { display: "notNull" },
            update: { disabled: true, selectPropOption: { reloadButton: false } },
          },
          ...brewingDefault(
            t,
            form,
            formMemo?.current,
            formFunction?.current,
            dataFunction?.current,
            params?.setTimer,
            params?.locale,
            "user",
            "result",
            false,
          ),
          condition: {
            label: "blank",
            labelClassName: dc.mobileHidden,
            itemClassName: "brewing_result",
            read: { display: "notNull", displayCheckIds: ["outside_air", "inside_air", "outside_hum", "inside_hum"] },
            fieldId: {
              condition_air: {
                ...dc.airTemp,
                readOnly: true,
                disabled: true,
                variant: "borderless",
                unit: "",
              },
              condition_hum: {
                ...dc.hum,
                readOnly: true,
                disabled: true,
                variant: "borderless",
                unit: "",
                read: { display: "notNull", displayCheckIds: ["outside_hum", "inside_hum"] },
              },
            },
          },
          outside: {
            itemClassName: "brewing_result",
            read: { display: "notNull", displayCheckIds: ["outside_air", "outside_hum"] },
            edit: { label: dc.weatherButton(form) },
            fieldId: {
              outside_air: dc.airTemp,
              outside_hum: { ...dc.hum, read: { display: "notNull", displayCheckIds: undefined } },
            },
          },
          inside: {
            itemClassName: "brewing_result",
            read: { display: "notNull", displayCheckIds: ["inside_air", "inside_hum"] },
            fieldId: {
              inside_air: dc.airTemp,
              inside_hum: { ...dc.hum, read: { display: "notNull", displayCheckIds: undefined } },
            },
          },

          brewing_split: {
            label: "blank",
            itemClassName: "!hidden",
          },
          ...qcCommonCols(form, formFunction?.current, dataFunction?.current, params?.locale, ["cupnote_name"], alertContext, ta, "Coffee"),
          public_type: { ...dc.publicType },
          public_uuid: { ...dc.publicUUID("/info/brewing/note/") },
          ...qcSubCols,
        },
        tempValue: { cupnote_place: "STR" },
        writeMatching: { type: "coffeebean_uuid_type", serial: "coffeebean_uuid_dummy" },
        listColLinkField: ["cupnote_date", "cupnote_seq"],
        base64Field: ["coffeebean_uuid"],
        listColDesc: [
          {
            field: "store_name",
            prev: { element: params?.locale === enLocale ? "store_name" : "store_name_locale", transition: true },
            unitPrev: " : ",
          },
          {
            field: "qc_summary",
            fieldId: {
              cupnote_flavor_list: { ...dc.selectMulti, codeGroup: "FLVR" },
              cupnote_aftertaste_list: { ...dc.selectMulti, codeGroup: "CNAFTST" },
              cupnote_texture_list: { ...dc.selectMulti, codeGroup: "CNTXTR" },
              cupnote_feeling_list: { ...dc.selectMulti, codeGroup: "CNFLNG" },
            },
          },
          {
            field: "cupnote_desc",
            prev: { element: "cupnote_desc", transition: true },
            unitPrev: " : ",
            itemClassName: "whitespace-pre-wrap text-start",
          },
        ],
        listColDescBold: ["cupnote_name"],
        listSearch: [
          { ...dc.date, field: "search0", label: "cupnote_date", placeholder: "cupnote_date", defaultValue: undefined },
          { ...dc.date, field: "search1", label: "blending_result_date", placeholder: "blending_result_date", defaultValue: undefined },
        ],

        listSearchSelect: [
          // { ...dc.input, field: "search_title_all", label: "cupnote_name", placeholder: "cupnote_name" },
          {
            ...dc.input,
            field: "cupnote_name",
            label: enLocale === params?.locale ? "cupnote_name" : "cupnote_name_locale",
            placeholder: enLocale === params?.locale ? "cupnote_name" : "cupnote_name_locale",
          },
          {
            ...dc.input,
            field: "store_name",
            label: enLocale === params?.locale ? "store_name" : "store_name_locale",
            placeholder: enLocale === params?.locale ? "store_name" : "store_name_locale",
          },
        ],
        listOrder: ["cupnote_date_orderdesc", "cupnote_date_orderasc", "blending_result_date_orderdesc", "blending_result_date_orderasc"],
        listCols: [
          // "roaster_seq",
          // "blending_seq",
          {
            field: "cupnote_date",
            fieldId: {
              cupnote_date: { ...dc.date, dateFormatter: props.isMobile ? "MM-DD" : "YYYY-MM-DD" },
              brewing_date_term: { prev: { element: "+", transition: false }, next: { element: "day_unit", transition: true } },
            },
            valueJoin: " / ",
          },

          // "blending_result_date",
          { field: "cupnote_name", label: koLocale === params?.locale ? "cupnote_name_locale" : "cupnote_name" },
          // "cupnote_seq",
          "cupnote_satis",
          // { field: "cupnote_date", ...dc.date, dateFormatter: props.isMobile ? "MM-DD" : "YYYY-MM-DD" },
          // { field: "brewing_date_term", unit: t("day_unit") },
        ],
        listColMobileTitle: "cupnote_name",
      };

    default:
      return undefined;
  }
};
